/**
 * TariffBuilder — конструктор тарифа «как у операторов связи» (секция 07).
 *
 * Идея владельца (2026-08-21): вместо выбора из фикс-планов клиент сам
 * собирает тариф — база 2 000 ₸ без столов (профиль самсаханы), ползунок
 * зала (главный платный модуль: столы + официант), ползунок сотрудников,
 * тумблеры доставка/AI/white-label. Пресеты-кнопки = те же ползунки,
 * выставленные заранее (гибрид: ленивые берут набор, нестандартные крутят).
 *
 * Цены: сетка `tariff_grid` из БД (RPC get_tariff_grid, правится в супер-админке →
 * Планы), фолбэк — DEFAULT_TARIFF_GRID из @cafe-os/utils с теми же числами.
 * Расчёт — computeTariff (единый для лендинга, кабинета кафе и SQL-двойника
 * tariff_config_price). Пресеты дают РОВНО цены планов из `plans` → бейдж
 * «= тариф …» берёт имя плана из БД по совпадению цены.
 *
 * CTA не подключает ничего сам: кладёт конфигурацию в leadNote (по-русски,
 * для менеджера) и скроллит к форме заявки — та прикладывает её в `note`.
 */
import { useState } from "react";
import { ArrowRight, Check } from "lucide-react";
import {
  DEFAULT_TARIFF_GRID,
  TARIFF_PRESETS,
  computeTariff,
  parseTariffGrid,
  type TariffConfig,
  type TariffGrid,
} from "@cafe-os/utils";
import { useLang } from "../i18n";
import { useReveal } from "../lib/motion";
import { setLeadNote } from "../lib/leadNote";
import { planNameByPrice, usePublicPlans, useTariffGrid } from "../lib/pricingTiers";
import { SectionHead } from "./SectionHead";

/** Русские ярлыки для note менеджеру (не зависят от языка посетителя). */
const HALL_RU = ["без зала (навынос)", "до 10 столов", "до 30 столов", "до 50 столов", "безлимит столов"];
const STAFF_RU = ["до 5 сотрудников", "до 10 сотрудников", "до 20 сотрудников", "безлимит сотрудников"];

type Config = TariffConfig;

const PRESETS: { key: "presetTakeaway" | "presetCafe" | "presetResto" | "presetMax"; cfg: Config }[] = [
  { key: "presetTakeaway", cfg: TARIFF_PRESETS.takeaway }, // только база
  { key: "presetCafe", cfg: TARIFF_PRESETS.cafe }, // = Стартер
  { key: "presetResto", cfg: TARIFF_PRESETS.resto }, // = Про
  { key: "presetMax", cfg: TARIFF_PRESETS.max }, // = Энтерпрайз
];

const fmt = (n: number) => n.toLocaleString("ru-RU");

export function TariffBuilder() {
  const { t, lang } = useLang();
  const b = t.builder;
  const ref = useReveal<HTMLElement>();
  const plans = usePublicPlans();
  const gridRaw = useTariffGrid();

  const [cfg, setCfg] = useState<Config>(PRESETS[1].cfg); // старт = «Кафе с залом»

  // Сетка из БД поверх дефолта (пока грузится / RPC недоступен — дефолт с теми же числами).
  const grid: TariffGrid = { ...DEFAULT_TARIFF_GRID, ...parseTariffGrid(gridRaw) };
  const BASE_PRICE = grid.base;
  const HALL_PRICES = [0, grid.hall_10, grid.hall_30, grid.hall_50, grid.hall_inf];
  const STAFF_PRICES = [0, grid.staff_10, grid.staff_20, grid.staff_inf];
  const MODULE_PRICES = { delivery: grid.mod_delivery, ai: grid.mod_ai, wl: grid.mod_wl };

  const total = computeTariff(cfg, grid).total;

  // Конфигурация совпала с готовым планом → бейдж «= тариф Х» (доверие: цены сходятся).
  // Имя — из БД (list_public_plans) по совпадению цены; фолбэк — статика i18n.
  const staticMatch: Record<number, string> = {
    10000: t.pricing.starter.name,
    15000: t.pricing.delivery.name,
    25000: t.pricing.pro.name,
    40000: t.pricing.enterprise.name,
  };
  const matchedPlan = planNameByPrice(plans, total, lang) ?? (plans ? undefined : staticMatch[total]);

  const activePreset = PRESETS.find(
    (p) =>
      p.cfg.hall === cfg.hall &&
      p.cfg.staff === cfg.staff &&
      p.cfg.delivery === cfg.delivery &&
      p.cfg.ai === cfg.ai &&
      p.cfg.wl === cfg.wl,
  )?.key;

  function submitToLead() {
    const modules = [
      cfg.delivery ? "доставка" : null,
      cfg.ai ? "AI-ассистент" : null,
      cfg.wl ? "свой бренд (white label)" : null,
    ].filter(Boolean);
    const lines = [
      `Конструктор тарифа: ${fmt(total)} ₸/мес`,
      `Зал: ${HALL_RU[cfg.hall]} · Сотрудники: ${STAFF_RU[cfg.staff]}`,
      modules.length ? `Модули: ${modules.join(", ")}` : "Модули: нет",
    ];
    if (matchedPlan) lines.push(`(= тариф «${matchedPlan}»)`);
    setLeadNote(lines.join("\n"));
    document.getElementById("lead")?.scrollIntoView({ behavior: "smooth" });
  }

  return (
    <section
      id="builder"
      ref={ref}
      className="relative overflow-hidden border-t"
      style={{ borderColor: "var(--line)" }}
    >
      {/* Золотое свечение справа — под сводку-итог */}
      <div
        className="pointer-events-none absolute inset-0"
        aria-hidden
        style={{
          background: "radial-gradient(44% 55% at 82% 45%, rgba(212,166,86,0.09), transparent 70%)",
        }}
      />

      <div
        className="relative mx-auto w-full max-w-7xl px-5 sm:px-8"
        style={{ paddingTop: "var(--space-section)", paddingBottom: "var(--space-section)" }}
      >
        <SectionHead num="07" label={b.label} title={b.title} accent={b.accent} aside={b.subtitle} />

        <div className="grid gap-8 lg:grid-cols-[1.15fr_0.85fr] lg:gap-12">
          {/* ───── Управление ───── */}
          <div className="space-y-5">
            {/* Пресеты */}
            <div className="reveal" data-reveal="">
              <div
                className="mb-3 text-[11px] font-bold uppercase tracking-[0.18em]"
                style={{ color: "var(--fg-muted)" }}
              >
                {b.presets}
              </div>
              <div className="flex flex-wrap gap-2.5">
                {PRESETS.map((p) => {
                  const isOn = activePreset === p.key;
                  return (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => setCfg(p.cfg)}
                      className="rounded-full border px-4 py-2 text-[12px] font-bold transition-colors"
                      style={
                        isOn
                          ? { background: "var(--gold)", color: "#1a1308", borderColor: "var(--gold)" }
                          : { borderColor: "var(--line-strong)", color: "var(--fg-soft)", background: "transparent" }
                      }
                    >
                      {b[p.key]}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* База */}
            <ControlCard
              title={b.base}
              hint={b.baseHint}
              price={`${fmt(BASE_PRICE)} ₸`}
              priceGold
            />

            {/* Зал */}
            <ControlCard
              title={b.hall}
              hint={b.hallHint}
              price={HALL_PRICES[cfg.hall] > 0 ? `+${fmt(HALL_PRICES[cfg.hall])} ₸` : b.free}
            >
              <TierSlider
                value={cfg.hall}
                options={b.hallOptions}
                onChange={(v) => setCfg({ ...cfg, hall: v as Config["hall"] })}
                ariaLabel={b.hall}
              />
            </ControlCard>

            {/* Сотрудники */}
            <ControlCard
              title={b.staff}
              hint={b.staffHint}
              price={STAFF_PRICES[cfg.staff] > 0 ? `+${fmt(STAFF_PRICES[cfg.staff])} ₸` : b.free}
            >
              <TierSlider
                value={cfg.staff}
                options={b.staffOptions}
                onChange={(v) => setCfg({ ...cfg, staff: v as Config["staff"] })}
                ariaLabel={b.staff}
              />
            </ControlCard>

            {/* Модули */}
            <div className="reveal space-y-2.5" data-reveal="">
              <div
                className="mb-1 text-[11px] font-bold uppercase tracking-[0.18em]"
                style={{ color: "var(--fg-muted)" }}
              >
                {b.modules}
              </div>
              <ModuleRow
                title={b.mDelivery}
                hint={b.mDeliveryHint}
                price={`+${fmt(MODULE_PRICES.delivery)} ₸`}
                on={cfg.delivery}
                onToggle={(v) => setCfg({ ...cfg, delivery: v })}
              />
              <ModuleRow
                title={b.mAi}
                hint={b.mAiHint}
                price={`+${fmt(MODULE_PRICES.ai)} ₸`}
                on={cfg.ai}
                onToggle={(v) => setCfg({ ...cfg, ai: v })}
              />
              <ModuleRow
                title={b.mWl}
                hint={b.mWlHint}
                price={`+${fmt(MODULE_PRICES.wl)} ₸`}
                on={cfg.wl}
                onToggle={(v) => setCfg({ ...cfg, wl: v })}
              />
            </div>
          </div>

          {/* ───── Итог ───── */}
          <aside className="reveal self-start lg:sticky lg:top-28" data-reveal="" style={{ "--reveal-delay": "150ms" } as React.CSSProperties}>
            <div
              className="rounded-3xl p-7 sm:p-8"
              style={{ background: "var(--bg-card)", border: "1px solid var(--gold-line)" }}
            >
              <div className="mb-1 flex items-center justify-between gap-3">
                <span className="t-eyebrow">{b.total}</span>
                {matchedPlan ? (
                  <span
                    className="rounded-full px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-[0.12em]"
                    style={{
                      background: "rgba(212,166,86,0.14)",
                      color: "var(--gold-bright)",
                      border: "1px solid var(--gold-line)",
                    }}
                  >
                    {b.matches} «{matchedPlan}»
                  </span>
                ) : null}
              </div>

              <div className="mb-6 flex items-baseline gap-2">
                <span key={total} className="t-brand tb-pop text-4xl sm:text-5xl" style={{ color: "var(--gold)" }}>
                  {fmt(total)} ₸
                </span>
                <span className="text-sm font-medium" style={{ color: "var(--fg-muted)" }}>
                  {b.perMonth}
                </span>
              </div>

              {/* Раскладка цены */}
              <div className="mb-6 space-y-2 border-t pt-5" style={{ borderColor: "var(--line)" }}>
                <SummaryRow label={b.base} value={`${fmt(BASE_PRICE)} ₸`} />
                {HALL_PRICES[cfg.hall] > 0 && (
                  <SummaryRow label={b.hallOptions[cfg.hall]} value={`+${fmt(HALL_PRICES[cfg.hall])} ₸`} />
                )}
                {STAFF_PRICES[cfg.staff] > 0 && (
                  <SummaryRow
                    label={`${b.staff}: ${b.staffOptions[cfg.staff].toLowerCase()}`}
                    value={`+${fmt(STAFF_PRICES[cfg.staff])} ₸`}
                  />
                )}
                {cfg.delivery && <SummaryRow label={b.mDelivery} value={`+${fmt(MODULE_PRICES.delivery)} ₸`} />}
                {cfg.ai && <SummaryRow label={b.mAi} value={`+${fmt(MODULE_PRICES.ai)} ₸`} />}
                {cfg.wl && <SummaryRow label={b.mWl} value={`+${fmt(MODULE_PRICES.wl)} ₸`} />}
              </div>

              {/* Что включено всегда */}
              <div className="mb-7 border-t pt-5" style={{ borderColor: "var(--line)" }}>
                <div
                  className="mb-3 text-[11px] font-bold uppercase tracking-[0.18em]"
                  style={{ color: "var(--fg-muted)" }}
                >
                  {b.included}
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-2">
                  {b.includedList.map((f) => (
                    <span key={f} className="flex items-start gap-1.5 text-xs" style={{ color: "var(--fg-soft)" }}>
                      <Check className="mt-0.5 h-3 w-3 flex-shrink-0" style={{ color: "var(--gold)" }} aria-hidden />
                      {f}
                    </span>
                  ))}
                </div>
              </div>

              <button type="button" onClick={submitToLead} className="btn-gold w-full">
                {b.cta}
                <ArrowRight className="h-4 w-4" aria-hidden />
              </button>
              <p className="mt-4 text-center text-xs leading-relaxed" style={{ color: "var(--fg-muted)" }}>
                {b.ctaHint}
              </p>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}

/** Карточка одного контрола: заголовок + подсказка + цена справа (+ children). */
function ControlCard({
  title,
  hint,
  price,
  priceGold,
  children,
}: {
  title: string;
  hint: string;
  price: string;
  priceGold?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <div
      className="reveal rounded-2xl p-5 sm:p-6"
      data-reveal=""
      style={{ background: "var(--bg-card)", border: "1px solid var(--line-strong)" }}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-[15px] font-extrabold" style={{ color: "var(--fg)" }}>
            {title}
          </div>
          <div className="mt-1 text-xs leading-relaxed" style={{ color: "var(--fg-muted)" }}>
            {hint}
          </div>
        </div>
        <span
          className="t-brand flex-shrink-0 text-base"
          style={{ color: priceGold ? "var(--gold)" : "var(--fg-soft)" }}
        >
          {price}
        </span>
      </div>
      {children}
    </div>
  );
}

/** Дискретный ползунок по ступеням тарифа + кликабельные подписи ступеней. */
function TierSlider({
  value,
  options,
  onChange,
  ariaLabel,
}: {
  value: number;
  options: string[];
  onChange: (v: number) => void;
  ariaLabel: string;
}) {
  const max = options.length - 1;
  const fill = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="mt-4">
      <input
        type="range"
        min={0}
        max={max}
        step={1}
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value, 10))}
        className="tb-range"
        aria-label={ariaLabel}
        aria-valuetext={options[value]}
        style={{ "--tb-fill": `${fill}%` } as React.CSSProperties}
      />
      <div className="mt-1.5 flex justify-between">
        {options.map((label, i) => (
          <button
            key={label}
            type="button"
            onClick={() => onChange(i)}
            className="max-w-[20%] text-left text-[10px] font-bold uppercase leading-tight tracking-[0.08em] transition-colors"
            style={{
              color: i === value ? "var(--gold-bright)" : "var(--fg-muted)",
              textAlign: i === 0 ? "left" : i === max ? "right" : "center",
            }}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Строка модуля с тумблером. */
function ModuleRow({
  title,
  hint,
  price,
  on,
  onToggle,
}: {
  title: string;
  hint: string;
  price: string;
  on: boolean;
  onToggle: (v: boolean) => void;
}) {
  return (
    <div
      className="flex items-center justify-between gap-4 rounded-2xl p-4 sm:p-5 transition-colors"
      style={{
        background: "var(--bg-card)",
        border: `1px solid ${on ? "var(--gold-line)" : "var(--line-strong)"}`,
      }}
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-baseline gap-x-2.5">
          <span className="text-[15px] font-extrabold" style={{ color: "var(--fg)" }}>
            {title}
          </span>
          <span className="t-brand text-sm" style={{ color: on ? "var(--gold)" : "var(--fg-muted)" }}>
            {price}
          </span>
        </div>
        <div className="mt-1 text-xs leading-relaxed" style={{ color: "var(--fg-muted)" }}>
          {hint}
        </div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label={title}
        onClick={() => onToggle(!on)}
        className="flex-shrink-0 rounded-full p-[3px] transition-colors"
        style={{
          width: 48,
          height: 27,
          background: on ? "var(--gold)" : "rgba(244,239,230,0.14)",
          display: "flex",
          alignItems: "center",
          justifyContent: on ? "flex-end" : "flex-start",
        }}
      >
        <span
          className="rounded-full transition-colors"
          style={{ width: 21, height: 21, background: on ? "#1a1308" : "var(--fg-muted)" }}
        />
      </button>
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <span style={{ color: "var(--fg-soft)" }}>{label}</span>
      <span className="t-brand text-[13px]" style={{ color: "var(--fg)" }}>
        {value}
      </span>
    </div>
  );
}
